import {
  Location,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import { HistoryDetailAlert } from "api/interfaces/alertInterface.interface";
import TableColumnFilterTitle from "components/blocks/table/TableColumnFilterTitle";
import { substractTime } from "utils/functions";
import TableButton from "components/atoms/buttons/TableButton";
import { useCallback, useMemo, useState } from "react";
import { getAlertHistoryListDetail } from "api/alertAPI";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { useQuery } from "react-query";
import {
  BasePagination,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { getRecorderTimeFormat } from "utils/timeUtil";
import moment from "moment";
import {
  AlertCategoryTypeFilter,
  AlertStatusTypeFilter,
  navigateAlertDetailPage,
} from "utils/AlertUtil";

import { StatusTxt } from "pages/alert/AlertStyled";
import { TableColumnsType } from "antd";
import {
  changeSortOrderAntd,
  CommonTable,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import { ColumnType } from "antd/es/table";
import AlertDisplay from "components/blocks/status/AlertDisplay";
import { useMenu } from "hook/useMenu";
import { useQueryKey } from "utils/queryUtil";
import { initialAlertHistoryDetailQuery } from "./AlertHistoryDetailPage";
import * as mediaQuery from "components/MediaQuery";
import useApiError from "hook/useApiError";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";

type Props = {};

export default function AlertHistoryDetail(props: Props): JSX.Element {
  const navigate = useNavigate();
  const location: Location = useLocation();
  const { queryNavigate } = useMenu({});
  const isMobile = mediaQuery.useIsMobile();

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { lang, notifyError } = useApiError();
  const {
    alertHistoryDetailQueryInfo,
    setAlertHistoryDetailQueryInfo,
    setListTotal,
    isPending,
    setIsPending,
    recorderId,
  } = useOutletContext<any>();

  const [data, setData] = useState<HistoryDetailAlert[]>([]);

  const queryKey = useQueryKey({ ...alertHistoryDetailQueryInfo });

  const alertDetailQuery = useQuery(
    ["alertHistoryDetail", queryKey, selectedAccount.accountId, recorderId],
    () =>
      getAlertHistoryListDetail({
        accountId: selectedAccount.accountId,
        recorderId: recorderId as string,
        payload: alertHistoryDetailQueryInfo,
      }),
    {
      retry: 0,
      enabled: recorderId !== undefined && selectedAccount.accountId !== "",
      onSuccess: (res: any) => {
        if (
          res.error !== undefined &&
          res.error === 0 &&
          res.result !== undefined
        ) {
          setData(res.result.alerts);
          setListTotal(res.page.total);
          setAlertHistoryDetailQueryInfo((info: BasePagination) => {
            return {
              ...info,
              ...res.page,
            };
          });
        } else {
          setData([]);
          setListTotal(0);
          setAlertHistoryDetailQueryInfo({ ...initialAlertHistoryDetailQuery });
        }
      },
      onError: (e: any) => {
        setData([]);
        setListTotal(0);
        setAlertHistoryDetailQueryInfo({ ...initialAlertHistoryDetailQuery });
        notifyError(lang.label.alertHistory.notify.search.fail);
      },
      onSettled: (e: any) => {
        setIsPending(false);
      },
    }
  );

  const onClickAlertCategoryType = useCallback(
    (value: string | undefined) => {
      if (value !== undefined) {
        if (value === "all") {
          if (alertHistoryDetailQueryInfo.category !== null) {
            queryNavigate({
              to: location.pathname,
              queryInfo: {
                ...(recorderId !== null && { recorderId }),
                pageLimit: alertHistoryDetailQueryInfo.pageLimit,
                pageNum: 0,
                keyword: alertHistoryDetailQueryInfo.keyword as string,
                sortType: alertHistoryDetailQueryInfo.sortType,
                sortDirection:
                  alertHistoryDetailQueryInfo.sortDirection as SORT_DIRECTION,
                category: null,
                status: alertHistoryDetailQueryInfo.status,
              },
            });
          } else {
            alertDetailQuery.refetch();
          }
          setIsPending(true);
          // setAlertHistoryDetailQueryInfo((info) => {
          //   return { ...info, category: null, pageNum: 0 };
          // });
        } else {
          if (alertHistoryDetailQueryInfo.category !== value) {
            queryNavigate({
              to: location.pathname,
              queryInfo: {
                ...(recorderId !== null && { recorderId }),
                pageLimit: alertHistoryDetailQueryInfo.pageLimit,
                pageNum: 0,
                keyword: alertHistoryDetailQueryInfo.keyword as string,
                sortType: alertHistoryDetailQueryInfo.sortType,
                sortDirection:
                  alertHistoryDetailQueryInfo.sortDirection as SORT_DIRECTION,
                category: value,
                status: alertHistoryDetailQueryInfo.status,
              },
            });
          } else {
            alertDetailQuery.refetch();
          }
          setIsPending(true);
          // setAlertHistoryDetailQueryInfo((info) => {
          //   return { ...info, category: value as string, pageNum: 0 };
          // });
        }
      }
    },
    [
      alertDetailQuery,
      alertHistoryDetailQueryInfo.category,
      alertHistoryDetailQueryInfo.keyword,
      alertHistoryDetailQueryInfo.pageLimit,
      alertHistoryDetailQueryInfo.sortDirection,
      alertHistoryDetailQueryInfo.sortType,
      alertHistoryDetailQueryInfo.status,
      location.pathname,
      queryNavigate,
    ]
  );

  const onClickAlertStatusType = useCallback(
    (value: string | undefined) => {
      if (value !== undefined) {
        if (value === "all") {
          if (alertHistoryDetailQueryInfo.status !== null) {
            queryNavigate({
              to: location.pathname,
              queryInfo: {
                ...(recorderId !== null && { recorderId }),
                pageLimit: alertHistoryDetailQueryInfo.pageLimit,
                pageNum: 0,
                keyword: alertHistoryDetailQueryInfo.keyword as string,
                sortType: alertHistoryDetailQueryInfo.sortType,
                sortDirection:
                  alertHistoryDetailQueryInfo.sortDirection as SORT_DIRECTION,
                category: alertHistoryDetailQueryInfo.category,
                status: null,
              },
            });
          } else {
            alertDetailQuery.refetch();
          }
          setIsPending(true);
          // setAlertHistoryDetailQueryInfo((info) => {
          //   return { ...info, status: null, pageNum: 0 };
          // });
        } else {
          if (alertHistoryDetailQueryInfo.status !== value) {
            queryNavigate({
              to: location.pathname,
              queryInfo: {
                ...(recorderId !== null && { recorderId }),
                pageLimit: alertHistoryDetailQueryInfo.pageLimit,
                pageNum: 0,
                keyword: alertHistoryDetailQueryInfo.keyword as string,
                sortType: alertHistoryDetailQueryInfo.sortType,
                sortDirection:
                  alertHistoryDetailQueryInfo.sortDirection as SORT_DIRECTION,
                category: alertHistoryDetailQueryInfo.category,
                status: value,
              },
            });
          } else {
            alertDetailQuery.refetch();
          }
          setIsPending(true);
          // setAlertHistoryDetailQueryInfo((info) => {
          //   return { ...info, status: value as string, pageNum: 0 };
          // });
        }
      }
    },
    [
      alertDetailQuery,
      alertHistoryDetailQueryInfo.category,
      alertHistoryDetailQueryInfo.keyword,
      alertHistoryDetailQueryInfo.pageLimit,
      alertHistoryDetailQueryInfo.sortDirection,
      alertHistoryDetailQueryInfo.sortType,
      alertHistoryDetailQueryInfo.status,
      location.pathname,
      queryNavigate,
    ]
  );
  const onHeaderCell = (column: ColumnType<HistoryDetailAlert>) => {
    return {
      onClick: () => {
        if (column.key) {
          setAlertHistoryDetailQueryInfo((info: BasePagination) => {
            return {
              ...info,
              sortType: column.key as string,
              sortDirection: onSortAntd(info.sortDirection),
              pageNum: 0,
            };
          });
        }
      },
    };
  };
  const detailColumns: TableColumnsType<HistoryDetailAlert> = [
    {
      title: (
        <TableColumnFilterTitle
          title="Category"
          filters={AlertCategoryTypeFilter}
          onClick={onClickAlertCategoryType}
          data-testid="alert-history-detail-table-column-filter-title-click-alert-category-type"
        />
      ),

      render: (value, row) => row.category,
      width: isMobile ? 60 : 110,
    },
    {
      title: "Rule Name",
      render: (value, row) => row.ruleName,
      responsive: ["md"],
    },
    {
      title: (
        <TableColumnFilterTitle
          title="Status"
          filters={AlertStatusTypeFilter}
          onClick={onClickAlertStatusType}
          data-testid="alert-history-detail-table-column-filter-title-click-alert-status-type"
        />
      ),

      render: (value, row) => (
        <StatusTxt string={row.status.toUpperCase()}>{row.status}</StatusTxt>
      ),

      width: isMobile ? 60 : 100,
    },
    {
      title: "Alert",
      render: (value, row) => (
        <AlertDisplay
          alertType={String(row.alertType)}
          cameraName={row.cameraName}
          channel={row.channel}
        />
      ),

      responsive: ["md"],
    },
    {
      title: "Trigger Time",
      key: "alertTrigger",
      render: (value, row) =>
        row.alertTriggered !== undefined
          ? getRecorderTimeFormat(
              row.alertTriggered,
              selectedAccount,
              row.timezone,
              true
            )
          : "",
      width: isMobile ? undefined : "15%",
      sortOrder: changeSortOrderAntd(
        alertHistoryDetailQueryInfo,
        "alertTrigger"
      ),
      sorter: true,
      onHeaderCell: onHeaderCell,
      defaultSortOrder: "descend",
    },
    {
      key: "alertClose",
      title: "Closed Time",
      render: (value, row) =>
        row.closedDate !== undefined
          ? getRecorderTimeFormat(
              row.closedDate,
              selectedAccount,
              row.timezone,
              true
            )
          : "",
      responsive: ["lg"],
      width: "15%",
    },
    {
      title: "Duration",
      render: (value, row) =>
        row.status === "CLOSED" || row.status === "Closed"
          ? substractTime(row.alertTriggered, row.closedDate)
          : substractTime(row.alertTriggered, moment()),
      responsive: ["xxl"],
      width: 100,
    },
    {
      title: "Resolved By",
      render: (value, row) =>
        row.resolvedUsername ? row.resolvedUsername : "",
      responsive: ["xl"],
      width: 150,
    },
    // 마지막 버튼
    {
      title: "",
      render: (value, row) => (
        <span className="row-click-button table-button-wrapper">
          <TableButton
            label={isMobile ? "View" : "View Alert"}
            onClick={() => onRowClick(row)}
            data-testid="alert-history-detail-table-button-row"
          />
        </span>
      ),

      align: "right",
      width: 100,
    },
  ];

  const onRowClick = (row: HistoryDetailAlert) => {
    navigateAlertDetailPage({
      location,
      navigate,
      accountId: selectedAccount.accountId as string,
      recorderId: recorderId ?? "",
      historyId: row.alertHistoryId,
      alertType: row.alertType,
    });
  };

  const onChangePage = useCallback(
    (page: number, totalRows: number) => {
      if (page !== alertHistoryDetailQueryInfo.pageNum + 1) {
        queryNavigate({
          to: location.pathname,
          queryInfo: {
            ...(recorderId !== null && { recorderId }),
            pageLimit: alertHistoryDetailQueryInfo.pageLimit,
            pageNum: page - 1,
            keyword: alertHistoryDetailQueryInfo.keyword as string,
            sortType: alertHistoryDetailQueryInfo.sortType,
            sortDirection:
              alertHistoryDetailQueryInfo.sortDirection as SORT_DIRECTION,
            category: alertHistoryDetailQueryInfo.category,
            status: alertHistoryDetailQueryInfo.status,
          },
        });
        setIsPending(true);
        // setAlertHistoryDetailQueryInfo((info) => {
        //   return { ...info, pageNum: (page - 1) as number };
        // });
      }
    },
    [
      alertHistoryDetailQueryInfo.category,
      alertHistoryDetailQueryInfo.keyword,
      alertHistoryDetailQueryInfo.pageLimit,
      alertHistoryDetailQueryInfo.pageNum,
      alertHistoryDetailQueryInfo.sortDirection,
      alertHistoryDetailQueryInfo.sortType,
      alertHistoryDetailQueryInfo.status,
      location.pathname,
      queryNavigate,
    ]
  );
  useStompDebouncedRefetch({
    stompKey: ["alertListUpdated"] as StompDateSignalKey[],
    refetch: alertDetailQuery.refetch,
  });

  const isLoading = useMemo(() => {
    return (
      !recorderId ||
      !selectedAccount.accountId ||
      alertDetailQuery.isLoading ||
      isPending
    );
  }, [
    recorderId,
    selectedAccount.accountId,
    alertDetailQuery.isLoading,
    isPending,
  ]);

  return (
    <CommonTable
      columns={detailColumns}
      dataSource={isLoading ? [] : data}
      queryInfo={alertHistoryDetailQueryInfo}
      onChangePage={onChangePage}
      loading={isLoading}
      rowKey="alertHistoryId"
    />
  );
}
